import { useState } from 'react'

import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/misc.tsx'
import { ResponsiveMarketingImage } from './ResponsiveMarketingImage'

export interface LearningPhaseHighlight {
	title: string
	description?: string
	icon?: string
	imageUrl?: string
	imageAlt?: string
}

export interface LearningPhaseCardProps {
	title?: string
	description?: string
	background?: 'default' | 'muted' | 'primary-light'
	highlights?: LearningPhaseHighlight[]
	imageUrl?: string
	imageAlt?: string
	embedded?: boolean
}

const backgroundStyles = {
	default: 'bg-background',
	muted: 'bg-muted/30',
	'primary-light': 'bg-primary/5',
}

const defaultHighlightIcons = [
	'mix',
	'mind-map',
	'check-circled',
	'component-1',
	'file-text',
	'pencil-2',
]

export function LearningPhaseCard({
	title,
	description,
	background = 'default',
	highlights = [],
	imageUrl,
	imageAlt,
	embedded = false,
}: LearningPhaseCardProps) {
	const [activeHighlightIndex, setActiveHighlightIndex] = useState(0)
	const highlightTabIndices = highlights
		.map((highlight, index) => (highlight.imageUrl ? index : -1))
		.filter((index) => index >= 0)
	const firstTabHighlightIndex = highlightTabIndices[0] ?? -1
	const resolvedActiveHighlightIndex = highlightTabIndices.includes(
		activeHighlightIndex,
	)
		? activeHighlightIndex
		: firstTabHighlightIndex

	const activeHighlight =
		resolvedActiveHighlightIndex >= 0
			? highlights[resolvedActiveHighlightIndex]
			: undefined
	const activeImageUrl =
		activeHighlight?.imageUrl ||
		imageUrl ||
		highlights.find((item) => item.imageUrl)?.imageUrl
	const activeImageAlt =
		activeHighlight?.imageAlt ||
		activeHighlight?.title ||
		imageAlt ||
		highlights.find((item) => item.imageAlt)?.imageAlt ||
		title

	const card = (
		<article className="space-y-5 py-2 md:space-y-6 md:py-3">
			<div className="space-y-2">
				{title && <h3 className="text-title-2 text-foreground">{title}</h3>}
				{description && (
					<div className="prose prose-sm md:prose-base prose-p:my-0 prose-p:font-semibold prose-p:text-muted-foreground max-w-2xl">
						<p>{description}</p>
					</div>
				)}
			</div>

			<div className="bg-muted rounded-md">
				<div
					className={cn(
						'grid gap-0 md:grid-cols-2',
						!activeImageUrl && 'md:grid-cols-1',
					)}
				>
					<div className="px-6 py-7 md:flex md:items-center md:px-8 md:py-10">
						<div className="divide-border/40 w-full divide-y">
							{highlights.map((highlight, highlightIndex) => {
								const isTabHighlight = Boolean(highlight.imageUrl)
								const isActive =
									isTabHighlight &&
									highlightIndex === resolvedActiveHighlightIndex
								const highlightContent = (
									<div className="flex items-start gap-3">
										<div
											className={cn(
												'border-border bg-muted text-foreground relative z-10 mt-0.5 flex w-fit items-center justify-center rounded-lg border p-2.5 transition-all',
												isActive &&
													'border-foreground bg-foreground text-background shadow-sm ring-1',
											)}
										>
											<Icon
												name={
													(highlight.icon ||
														defaultHighlightIcons[
															highlightIndex % defaultHighlightIcons.length
														]) as any
												}
												className="size-4"
											/>
										</div>
										<div>
											<p
												className={cn(
													'text-foreground text-base font-semibold transition-colors md:text-lg',
													isActive && 'text-primary',
												)}
											>
												{highlight.title}
											</p>
											{highlight.description && (
												<p className="text-muted-foreground mt-1 text-sm leading-relaxed md:text-base">
													{highlight.description}
												</p>
											)}
										</div>
									</div>
								)

								if (!isTabHighlight) {
									return (
										<div
											key={`${highlight.title}-${highlightIndex}`}
											className="block w-full py-4 text-left first:pt-0 last:pb-0"
										>
											{highlightContent}
										</div>
									)
								}

								return (
									<button
										type="button"
										key={`${highlight.title}-${highlightIndex}`}
										onClick={() => setActiveHighlightIndex(highlightIndex)}
										className="group block w-full cursor-pointer rounded-md py-4 text-left transition-colors first:pt-0 last:pb-0"
									>
										{highlightContent}
									</button>
								)
							})}
						</div>
					</div>

					{activeImageUrl && (
						<div className="border-border/50 border-t md:border-t-0 md:border-l">
							<div className="h-full min-h-[260px] md:min-h-[320px]">
								<ResponsiveMarketingImage
									src={activeImageUrl}
									alt={activeImageAlt ?? title ?? 'Learning phase image'}
									className="h-full w-full object-cover"
									width={1600}
									height={1000}
									sizes="(min-width: 768px) 50vw, 100vw"
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		</article>
	)

	if (embedded) {
		return card
	}

	return (
		<section className={cn('py-8 md:py-10', backgroundStyles[background])}>
			<div className="container mx-auto max-w-6xl px-4 md:px-6">{card}</div>
		</section>
	)
}
